import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { isPresent } from '@ember/utils';
import type Store from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { Changeset } from 'ember-changeset';
import type { DetailedChangeset } from 'ember-changeset/types';
import type { Task } from 'ember-concurrency';
import { dropTask } from 'ember-concurrency';
import PowerSelectMultiple from 'ember-power-select/components/power-select-multiple';
import { and, not, or } from 'ember-truth-helpers';
import SettingsPanel from 'garaje/components/ui/classic/settings-panel';
import TaskButton from 'garaje/components/ui/classic/task-button';
import UiButton from 'garaje/components/ui-button';
import type LocationModel from 'garaje/models/location';
import type UserModel from 'garaje/models/user';
import type FlashMessagesService from 'garaje/services/flash-messages';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import isEqual from 'lodash/isEqual';

interface ConnectNotificationsVisitorArgs {
  location: LocationModel;
  searchUsersTask: Task<unknown, [term: string, contacts: UserModel[]]>;
}

interface ConnectNotificationsVisitorSignature {
  Args: ConnectNotificationsVisitorArgs;
  Element: HTMLDivElement;
}

export default class ConnectNotificationsVisitor extends Component<ConnectNotificationsVisitorSignature> {
  @service declare store: Store;
  @service declare flashMessages: FlashMessagesService;

  get contacts(): UserModel[] {
    if (this.loadContactsTask.isRunning) return [];
    return this.location.multiTenancyVisitorContacts.content
      ? this.location.multiTenancyVisitorContacts.content
      : <UserModel[]>(<unknown>this.location.multiTenancyVisitorContacts);
  }

  @tracked _contactIds: string[] = [];
  @tracked isOpen = false;
  @tracked location!: DetailedChangeset<LocationModel>;

  get contactsChanged(): boolean {
    return !isEqual(
      this._contactIds,
      this.contacts.map((contact) => contact.id),
    );
  }

  constructor(owner: unknown, args: ConnectNotificationsVisitorArgs) {
    super(owner, args);
    void this.loadContactsTask.perform(this.args.location);
  }

  loadContactsTask = dropTask(async (location: LocationModel) => {
    try {
      const users = await location.multiTenancyVisitorContacts;
      this._contactIds = users.map((user) => user.id);
      this.location = Changeset(this.args.location);
    } catch (e) {
      this.flashMessages.showAndHideFlash('error', parseErrorForDisplay(e));
    }
  });

  enableTask = dropTask(async () => {
    if (!this.contacts.length) {
      this.isOpen = true;
      return;
    }

    await this.saveLocation(true);
    this.isOpen = true;
  });

  saveTask = dropTask(async () => {
    await this.saveLocation(!!this.contacts.length);
    void this.loadContactsTask.perform(this.args.location);
  });

  searchTask = dropTask(async (term: string) => {
    return await this.args.searchUsersTask.perform(term, this.contacts);
  });

  @action
  async searchUsers(term: string): Promise<UserModel[]> {
    const result = await this.searchTask.perform(term);
    return result as UserModel[];
  }

  @action
  async saveLocation(enabled: boolean): Promise<void> {
    try {
      const contactsChanged = this.location.changes.some((change) => change['key'] === 'multiTenancyVisitorContacts');

      if (isPresent(enabled)) {
        this.location.multiTenancyVisitorNotificationsEnabled = enabled;
      }

      await this.location.save();

      if (!enabled) {
        this.isOpen = false;
      }

      if (contactsChanged) {
        this.flashMessages.showAndHideFlash('success', 'Saved!');
      }
    } catch (e) {
      this.location.rollback();
      this.flashMessages.showAndHideFlash('error', parseErrorForDisplay(e));
    }
  }

  @action
  onDestroy(): void {
    this.location.rollback();
  }

  @action
  cancel(): void {
    this.isOpen = false;
    this.location.rollback();
  }

  @action
  onEditButtonClicked(): void {
    this.isOpen = true;
  }

  @action
  onMultiTenancyVisitorContactsChanged(contacts: UserModel[]): void {
    // Cast location to work around Ember Data type constraints
    ((<unknown>this.location) as { multiTenancyVisitorContacts: UserModel[] }).multiTenancyVisitorContacts = contacts;
  }

  <template>
    <SettingsPanel data-test-notifications-connect @isOpen={{this.isOpen}} ...attributes as |panel|>
      <panel.header
        @iconUri="/assets/images/features/notifications.svg"
        @title="Admin notifications for lobby sign in"
        @description="Get notified when connected accounts mark visitors as checked-in."
        @onCancel={{this.cancel}}
        as |header|
      >
        <header.controls>
          {{#if panel.isOpen}}
            <TaskButton
              class="btn btn--small btn--brand"
              @disabled={{or (not this.contactsChanged) this.loadContactsTask.isRunning}}
              @task={{this.saveTask}}
              @text="Save"
              @runningText="Saving..."
              data-test-save-connect-notifications
            />
          {{else if (and @location.multiTenancyVisitorNotificationsEnabled (not this.enableTask.isRunning))}}
            <UiButton
              class="btn btn--small btn--brand"
              {{on "click" this.onEditButtonClicked}}
              data-test-edit-connect-notifications
            >
              Edit
            </UiButton>
          {{else}}
            <TaskButton
              class="btn btn--small btn--brand"
              @task={{this.enableTask}}
              @disabled={{this.loadContactsTask.isRunning}}
              @text="Enable"
              @runningText="Enabling..."
              data-test-enable-connect-notifications
            />
          {{/if}}
        </header.controls>
      </panel.header>

      <panel.body class="mt-8">
        <div class="mx-16">
          <label id="multi-tenancy-contacts">Connect notification contact(s)</label>
          <PowerSelectMultiple
            data-test-connect-contacts-select
            class="w-10/12"
            @placeholder="Choose a contact"
            @ariaDescribedBy="multi-tenancy-contacts"
            @allowClear={{true}}
            @selected={{this.contacts}}
            @searchEnabled={{true}}
            @search={{this.searchUsers}}
            @onChange={{this.onMultiTenancyVisitorContactsChanged}}
            @disabled={{or this.loadContactsTask.isRunning this.saveTask.isRunning}}
            @searchField="fullName"
            as |user|
          >
            {{user.fullName}}
          </PowerSelectMultiple>
        </div>
        {{#if @location.multiTenancyVisitorNotificationsEnabled}}
          <div class="flex justify-end">
            <TaskButton
              data-test-connect-disable-button
              class="btn btn--small btn--outlineFill"
              @text="Disable"
              @runningText="Disabling..."
              @task={{fn this.saveLocation false}}
              @disabled={{or this.saveTask.isRunning this.loadContactsTask.isRunning}}
            />
          </div>
        {{/if}}
      </panel.body>
    </SettingsPanel>
  </template>
}
